<template>
  <div class="draft-attachment-field">
    <label v-if="label">{{ label }}</label>
    <div
      class="draft-attachment-uploader"
      :class="{ 'is-drag-over': dragOver }"
      @click="triggerInput"
      @dragenter.prevent="dragOver = true"
      @dragover.prevent="dragOver = true"
      @dragleave.prevent="dragOver = false"
      @drop.prevent="handleDrop"
    >
      <input
        ref="fileInput"
        type="file"
        multiple
        class="draft-attachment-input"
        @change="handleSelect"
      />
      <strong>{{ dropText }}</strong>
      <span>{{ sizeText }}</span>
    </div>
    <div v-if="uploading" class="muted">{{ uploadingText }}</div>
    <div v-if="error" class="error">{{ error }}</div>
    <div v-if="attachments.length" class="draft-attachments-list">
      <div v-for="attachment in attachments" :key="attachment.id" class="draft-attachment-item">
        <a
          :href="attachment.download_url || attachment.downloadUrl"
          class="draft-attachment-name"
          target="_blank"
          rel="noopener noreferrer"
        >
          {{ attachment.filename }}
        </a>
        <span class="draft-attachment-size">{{
          attachment.human_size || attachment.humanSize
        }}</span>
        <button
          type="button"
          class="btn-icon-small draft-attachment-delete"
          :title="deleteTitle"
          :disabled="uploading"
          @click.stop="removeAttachment(attachment)"
        >
          <TrashIcon />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import TrashIcon from './icons/TrashIcon.vue'

const props = defineProps({
  attachmentDraftId: {
    type: [String, Number],
    default: null,
  },
  attachments: {
    type: Array,
    default: () => [],
  },
  projectId: {
    type: [String, Number],
    default: null,
  },
  purpose: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    default: '',
  },
  dropText: {
    type: String,
    required: true,
  },
  sizeText: {
    type: String,
    required: true,
  },
  uploadingText: {
    type: String,
    required: true,
  },
  uploadErrorText: {
    type: String,
    required: true,
  },
  deleteErrorText: {
    type: String,
    required: true,
  },
  deleteTitle: {
    type: String,
    required: true,
  },
})

const emit = defineEmits(['update:attachmentDraftId', 'update:attachments'])

const fileInput = ref(null)
const uploading = ref(false)
const error = ref('')
const dragOver = ref(false)

function triggerInput() {
  if (uploading.value) return

  fileInput.value?.click()
}

function handleSelect(event) {
  uploadAttachments(Array.from(event.target.files || []))
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

function handleDrop(event) {
  dragOver.value = false
  uploadAttachments(Array.from(event.dataTransfer?.files || []))
}

async function uploadAttachments(files) {
  if (!files.length) return

  uploading.value = true
  error.value = ''

  try {
    const formData = new FormData()
    files.forEach(file => formData.append('files[]', file))
    appendContext(formData)

    const response = await fetch('/attachments/inline', {
      method: 'POST',
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || '',
      },
      body: formData,
      credentials: 'same-origin',
    })
    const data = await response.json()

    if (!response.ok || data.errors?.length) {
      throw new Error(data.errors?.join(', ') || props.uploadErrorText)
    }

    if (data.attachment_draft_id) {
      emit('update:attachmentDraftId', data.attachment_draft_id)
    }
    emit('update:attachments', [...props.attachments, ...(data.attachments || [])])
  } catch (uploadError) {
    error.value = uploadError.message || props.uploadErrorText
  } finally {
    uploading.value = false
  }
}

function appendContext(formData) {
  if (props.attachmentDraftId) {
    formData.append('attachment_draft_id', props.attachmentDraftId)
  } else if (props.projectId) {
    formData.append('project_id', props.projectId)
  }

  formData.append('purpose', props.purpose)
}

async function removeAttachment(attachment) {
  error.value = ''

  try {
    const response = await fetch(`/attachments/${attachment.id}`, {
      method: 'DELETE',
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || '',
      },
      credentials: 'same-origin',
    })

    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      throw new Error(data.errors?.join(', ') || props.deleteErrorText)
    }

    emit(
      'update:attachments',
      props.attachments.filter(item => item.id !== attachment.id)
    )
  } catch (deleteError) {
    error.value = deleteError.message || props.deleteErrorText
  }
}

defineExpose({
  reset() {
    error.value = ''
    dragOver.value = false
  },
})
</script>

<style scoped>
.draft-attachment-uploader {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 18px;
  border: 1px dashed #b7c7d6;
  border-radius: 10px;
  background: #f8fafc;
  color: #333;
  cursor: pointer;
  text-align: center;
}

.draft-attachment-uploader.is-drag-over {
  border-color: #2aabee;
  background: #eef8fc;
}

.draft-attachment-uploader span {
  color: #666;
  font-size: 12px;
}

.draft-attachment-input {
  display: none;
}

.draft-attachments-list {
  display: grid;
  gap: 8px;
  margin-top: 10px;
}

.draft-attachment-item {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
}

.draft-attachment-name {
  min-width: 0;
  overflow: hidden;
  color: #2aabee;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.draft-attachment-size {
  color: #666;
  font-size: 12px;
  white-space: nowrap;
}

.draft-attachment-delete {
  background: #fff;
  color: #666;
  border: 1px solid #ddd;
}

.draft-attachment-delete:hover:not(:disabled) {
  background: #f5f5f5;
  color: #333;
}
</style>
